/**
 * Zero-result recovery: when a search with filters returns nothing, list each
 * active filter as a "remove" link so the seeker can widen the search in one
 * tap instead of retyping it.
 *
 * Why: 2026-10-18→31 PostHog `zero_result_alert_shown` (human traffic) — 30
 * people (22% of searchers) hit an empty result. The failing URLs stacked
 * filters (`q=data entry&loc=Amerika`, `q=Customer support&…&posted=24h`)
 * while the same `q` alone returned 10+ live jobs. The old empty state blamed
 * the keyword ("No jobs for X yet") and offered no way to drop a filter.
 *
 * Pure (no React, no server imports) so the client empty state and its tests
 * share one implementation.
 */

/** URL params that narrow a search. `q` is the search itself, never relaxed here. */
export const RELAXABLE_FILTER_KEYS = [
	"loc",
	"posted",
	"level",
	"salary",
	"type",
	"eligibility",
	"remote",
	"competition",
	"category",
	"since",
] as const;

export type RelaxableFilterKey = (typeof RELAXABLE_FILTER_KEYS)[number];

export type RelaxFilterChip = {
	key: RelaxableFilterKey;
	/** What the seeker sees, e.g. "Posted: 24h". */
	label: string;
	/** Same page, same `q`, every other filter kept, this one removed. */
	href: string;
};

export type ZeroResultRelaxation = {
	chips: RelaxFilterChip[];
	/** Same page with `q` kept and every relaxable filter removed. Null when no filter is set. */
	clearAllHref: string | null;
};

const LABELS: Record<"en" | "id", Record<RelaxableFilterKey, string>> = {
	en: {
		loc: "Location",
		posted: "Posted",
		level: "Level",
		salary: "Salary",
		type: "Job type",
		eligibility: "Eligibility",
		remote: "Remote",
		competition: "Competition",
		category: "Category",
		since: "New since last visit",
	},
	id: {
		loc: "Lokasi",
		posted: "Diposting",
		level: "Level",
		salary: "Gaji",
		type: "Jenis kerja",
		eligibility: "Kelayakan",
		remote: "Remote",
		competition: "Persaingan",
		category: "Kategori",
		since: "Baru sejak kunjungan terakhir",
	},
};

const MAX_VALUE_CHARS = 24;

function displayValue(key: RelaxableFilterKey, raw: string): string | null {
	if (key === "since") return null; // a timestamp means nothing to a person
	const v = raw.trim().replace(/[-_]+/g, " ");
	return v.length > MAX_VALUE_CHARS ? `${v.slice(0, MAX_VALUE_CHARS - 1)}…` : v;
}

function hrefFor(pathname: string, params: URLSearchParams): string {
	const qs = params.toString();
	return `${pathname}${qs ? `?${qs}` : ""}#feed`;
}

/**
 * @param pathname current path, e.g. `/id/jobs`
 * @param search   current query string, with or without the leading `?`
 * @param locale   `en` gets English labels, anything else Indonesian
 */
export function buildZeroResultRelaxation(
	pathname: string,
	search: string,
	locale: string,
): ZeroResultRelaxation {
	const current = new URLSearchParams(search);
	current.delete("page");
	const labels = LABELS[locale === "en" ? "en" : "id"];

	const chips: RelaxFilterChip[] = [];
	for (const key of RELAXABLE_FILTER_KEYS) {
		const raw = current.get(key);
		if (raw === null || raw.trim() === "" || raw === "any") continue;
		const next = new URLSearchParams(current);
		next.delete(key);
		const value = displayValue(key, raw);
		chips.push({
			key,
			label: value ? `${labels[key]}: ${value}` : labels[key],
			href: hrefFor(pathname, next),
		});
	}

	if (chips.length === 0) return { chips, clearAllHref: null };

	const cleared = new URLSearchParams(current);
	for (const key of RELAXABLE_FILTER_KEYS) cleared.delete(key);
	return { chips, clearAllHref: hrefFor(pathname, cleared) };
}
